/* BEGIN_COPYRIGHT_HEADER

Copyright Vspry International Limited (c) 2020
All rights reserved.

END_COPYRIGHT_HEADER */

import { ReactNode, useEffect } from 'react'
import { FlexBox } from 'vspry-style-components'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'

import theme from 'styles/theme'
import Logo from 'components/Logo'
import auth from 'services/auth'
import useResponsive from 'hooks/responsive'
import PageFooter from './PageFooter'

const Inner = styled(FlexBox)<{ size?: keyof NonNullable<typeof screens> }>`
    max-width: ${(props) => (props.size ? screens?.[props.size] : screens?.small)}px;
    z-index: 10;
    position: relative;
`

const Header = styled(FlexBox)<{ $isMobile: boolean }>`
    position: fixed;
    height: 82px;
    top: 0;
    background-color: ${(props) => (props.$isMobile ? props.theme.accent : 'transparent')};
`

const Core = styled(FlexBox)`
    position: relative;
    margin-top: 82px;
    color: ${(props) => props.theme.text};
    background-color: ${(props) => props.theme.secondaryBG};
    min-height: calc(100vh - 82px);
    text-align: center;
    border-radius: 20px 20px 0px 0px;
    @media (max-width: ${screens.medium}px) {
        border-radius: 0px;
    }
    /* overflow: hidden; */
    contain: paint;
    padding-bottom: 100px;
`

const CircleContainer = styled.div`
    position: absolute;
    top: -30vw;
    left: -50vw;
    width: 100vw;
    height: 100vw;
    background: ${(props) => props.theme.accent};
    border-radius: 50%;
    opacity: 0.1;
`

const LogoPill = styled.div`
    background-color: #fafafa;
    padding: 1px 10px;
    border-radius: 9999px;
    display: inline-flex;
    overflow: hidden;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
    width: 160px;
    height: 53px;
`

type PageContainerProps = {
    children: ReactNode
    // eslint-disable-next-line react/no-unused-prop-types
    title: string
    size?: keyof NonNullable<typeof screens>
    smallPadding?: boolean
    noPadding?: boolean
    circleBackground?: boolean
}
export default function PageContainer({ children, size, smallPadding, noPadding, circleBackground }: PageContainerProps) {
    const navigate = useNavigate()
    const isMobile = useResponsive([screens.medium]) <= screens.medium

    useEffect(() => {
        auth.getUser().then((d) => !d && navigate('/'))
    }, [])

    return (
        <>
            <Header
                $isMobile={isMobile}
                direction='column'
                justify='center'
                align={theme.logoHeaderLocation === 'center' ? 'center' : 'flex-start'}
                padding='medium'
                width='100%'
            >
                {isMobile ? (
                    <LogoPill>
                        <Logo path={theme.headingLogoPath} size='125px' onClick={() => navigate('/')} />
                    </LogoPill>
                ) : (
                    <Logo path={theme.headingLogoPath} size='125px' onClick={() => navigate('/')} />
                )}
            </Header>
            <Core direction='column' align='center' justify='space-between' width='100%'>
                {circleBackground && <CircleContainer />}
                <Inner
                    justify='flex-start'
                    // eslint-disable-next-line no-nested-ternary
                    padding={noPadding ? undefined : smallPadding ? 'small' : 'large'}
                    direction='column'
                    width='100%'
                    grow='1'
                    size={size}
                >
                    {children}
                </Inner>
                <PageFooter />
            </Core>
        </>
    )
}
